import { isAnswered, type ReportAnswer, type ReportAnswers } from './report-checks.js'

/**
 * Composes a first draft of a long answer from the short ones already given.
 *
 * The mockup had one hand-written function per field, which is exactly the
 * thing decision 54 rules out: a new report type would then need a deployment
 * to get its drafts. So the sentence lives on `FormField.draftTemplate` as a
 * string the office can edit, and this renders it.
 *
 * Nothing here writes. A draft is offered in the review sheet, arrives ticked,
 * and costs one tap to reject. Decision 55.
 *
 * ## The template language
 *
 * `{key}` inserts an answer. Arrays join with ", ".
 *
 * `{key:filter}` transforms it, filters applying left to right:
 *
 * - `lower`, `upper` — case
 * - `list` — join an array with ", "
 * - `join(sep)` — join an array with `sep`
 * - `except(a;b;c)` — the listed items *absent* from an array answer, which is
 *   how "which areas did you not reach" comes out of "which areas did you
 *   reach"
 *
 * `{key|fallback}` uses `fallback` when the answer is blank.
 *
 * `[[…]]` marks a stretch as optional: it is dropped whole if any token inside
 * it has no answer and no fallback. This is how a clause like
 * "[[, consistent with {species}]]" disappears rather than trailing a comma.
 *
 * A draft is offered only when every token outside an optional group resolved,
 * and at least one token resolved from a real answer. So the field stays empty
 * until there is something to build a sentence out of, rather than offering the
 * technician their own fallbacks back.
 */

type Resolution = {
  text: string
  /** Whether a real answer produced this, as opposed to the fallback. */
  fromAnswer: boolean
  /** Whether anything at all produced it. */
  resolved: boolean
}

const TOKEN = /\{([^{}]+)\}/g
const OPTIONAL = /\[\[([^[\]]*)\]\]/g

function applyFilter(value: ReportAnswer, filter: string): ReportAnswer {
  const call = /^(\w+)\((.*)\)$/.exec(filter)
  const name = call ? (call[1] as string) : filter
  const argument = call ? (call[2] as string) : null

  switch (name) {
    case 'lower':
      return Array.isArray(value) ? value.map((item) => item.toLowerCase()) : String(value ?? '').toLowerCase()
    case 'upper':
      return Array.isArray(value) ? value.map((item) => item.toUpperCase()) : String(value ?? '').toUpperCase()
    case 'list':
      return Array.isArray(value) ? value.join(', ') : value
    case 'join':
      return Array.isArray(value) ? value.join(argument ?? ', ') : value
    case 'except': {
      const candidates = (argument ?? '').split(';').map((item) => item.trim()).filter(Boolean)
      if (!Array.isArray(value)) return candidates
      const chosen = new Set(value)
      return candidates.filter((candidate) => !chosen.has(candidate))
    }
    default:
      // An unknown filter passes the value through rather than blanking the
      // draft, so a typo in a template degrades to a plainer sentence.
      return value
  }
}

function flatten(value: ReportAnswer): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

function resolve(spec: string, answers: ReportAnswers): Resolution {
  const pipe = spec.indexOf('|')
  const body = (pipe === -1 ? spec : spec.slice(0, pipe)).trim()
  const fallback = pipe === -1 ? null : spec.slice(pipe + 1).trim()

  const [key, ...filters] = body.split(':').map((part) => part.trim())
  const field = key ?? ''

  let value: ReportAnswer = answers[field]

  // `except` inverts the sense of a multi-select, so it speaks for a field that
  // was answered by *not* choosing something. An untouched field is still
  // untouched; a field cleared to `[]` is an answer.
  const inverted = filters.some((filter) => filter.startsWith('except'))
  const answered = inverted
    ? Object.prototype.hasOwnProperty.call(answers, field)
    : isAnswered(value)

  for (const filter of filters) {
    if (filter) value = applyFilter(value, filter)
  }

  const text = flatten(value).trim()
  if (text.length > 0) return { text, fromAnswer: answered, resolved: true }
  if (fallback !== null && fallback.length > 0) return { text: fallback, fromAnswer: false, resolved: true }
  return { text: '', fromAnswer: false, resolved: false }
}

/** Whitespace and punctuation left behind when an optional group is dropped. */
function tidy(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/([,;:])\s*([.!?])/g, '$2')
    .replace(/\(\s*\)/g, '')
    .trim()
}

/**
 * Renders a draft, or null when there is not enough answered to build one.
 */
export function renderDraft(template: string | null | undefined, answers: ReportAnswers): string | null {
  if (!template) return null

  let anyFromAnswer = false
  let missingOutsideGroup = false

  const withGroups = template.replace(OPTIONAL, (_whole, inner: string) => {
    let groupComplete = true
    let groupFromAnswer = false

    const rendered = inner.replace(TOKEN, (_token, spec: string) => {
      const resolution = resolve(spec, answers)
      if (!resolution.resolved) groupComplete = false
      if (resolution.fromAnswer) groupFromAnswer = true
      return resolution.text
    })

    if (!groupComplete) return ''
    if (groupFromAnswer) anyFromAnswer = true
    return rendered
  })

  const output = withGroups.replace(TOKEN, (_token, spec: string) => {
    const resolution = resolve(spec, answers)
    if (!resolution.resolved) missingOutsideGroup = true
    if (resolution.fromAnswer) anyFromAnswer = true
    return resolution.text
  })

  if (missingOutsideGroup) return null
  if (!anyFromAnswer) return null

  const tidied = tidy(output)
  return tidied.length > 0 ? tidied : null
}
